
/**
 * Module dependencies.
 */

import { MenuLink, useFooterLinks } from 'src/core/menu';
import { color, media, units } from 'src/styles/utils';
import { navElementAttrs } from 'src/components/navbar/nav-element';
import { theme } from 'styled-tools';
import { useTranslate } from 'src/core/utils/translator';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement } from 'react';
import map from 'lodash/map';
import styled from 'styled-components';

/**
 * Current year.
 */

const currentYear = new Date().getFullYear();

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  color: ${color('white')};
  display: grid;
  font-size: 12px;
  gap: ${units(4)};
  line-height: 1.5;

  ${media.min('md')`
    align-items: center;
    gap: ${units(2)};
    grid-template-columns: auto 1fr;
  `}
`;

/**
 * `Copyright` styled component.
 */

const Copyright = styled.p`
  margin: 0;
  white-space: nowrap;
`;

/**
 * `Links` styled component.
 */

const Links = styled.div`
  column-gap: ${units(2)};
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: ${units(2)};

  ${media.min('md')`
    align-items: center;
    display: flex;
    justify-content: space-between;
  `}
`;

/**
 * `CreditsLink` styled component.
 */

const CreditsLink = styled.a.attrs(navElementAttrs)`
  -webkit-tap-highlight-color: transparent;
  color: ${color('white')};
  outline: none;
  text-decoration: underline;
  transition: color ${theme('animations.fastTransition')};
  white-space: nowrap;

  &:focus-visible,
  &:hover {
    color: ${color.transparentize('white', 0.7)};
  }
`;

/**
 * `Note` styled component.
 */

const Note = styled.p`
  grid-column: 1 / -1;
  margin: 0;

  ${media.min('md')`
    white-space: nowrap;
  `}
`;

/**
 * `Credits` component.
 */

const Credits = (): ReactElement => {
  const { translate } = useTranslate();
  const links = useFooterLinks();

  return (
    <Wrapper>
      <Copyright>
        {`Revigrés © ${currentYear}`}
      </Copyright>

      <Links>
        {map(links, ({ href, label }: MenuLink) => (
          <CreditsLink
            href={href}
            key={href}
          >
            {label}
          </CreditsLink>
        ))}

        {/*
          The design marks the note with an asterisk, but `PhoneLink` already
          marks every phone number on the site with `[1]`. Keeping one footnote
          convention matters more here than the glyph.
        */}
        <RawHtml element={Note}>
          {translate('footer:callCosts')}
        </RawHtml>
      </Links>
    </Wrapper>
  );
};

/**
 * Export `Credits` component.
 */

export default Credits;
